import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import styled from "styled-components";
import { getBaseMapStyle } from "../utils/mapStyles";
import RunPopup from "./RunPopup";
import NewRunPopup from "./NewRunPopup";
import RunMarkers from "./RunMarkers";
import { addGeoJSONLayers, fitBounds } from "../utils/mapRenderUtils";

const MapContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
`;

export const StyledMarker = styled.div`
  width: 12px;
  height: 12px;
  background: ${(props) => props.color || "#005fcc"};
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
`;

const PopupContent = styled.div`
  padding: 10px;
  min-width: 200px;
`;

const Input = styled.input`
  width: 100%;
  margin-bottom: 8px;
  padding: 4px;
  box-sizing: border-box;
`;

const Select = styled.select`
  width: 100%;
  margin-bottom: 8px;
  padding: 4px;
`;

const Button = styled.button`
  width: 100%;
  padding: 4px;
  background: #005fcc;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #004999;
  }
`;

const PopupContainer = styled.div`
  position: absolute;
  z-index: 10;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 12px;
  min-width: 220px;
  pointer-events: auto;
`;

export default function MapView({
  basemap,
  geojsonData,
  hoverLocation,
  showLines,
  showMarkers,
  show3D,
  selectedResortId,
  resortRuns,
  setResortRuns,
  visualizationSettings,
}) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const popupRef = useRef(null);
  const hoverMarkerRef = useRef(null);
  const clickHandlerRef = useRef(null);
  const styleLoadingRef = useRef(false);
  const prevSelectedResortId = useRef();
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [markerScreenPositions, setMarkerScreenPositions] = useState([]);
  const [newRunPosition, setNewRunPosition] = useState(null);

  // Add escape key handler
  const handleKeyDown = (e) => {
    if (e.key === "Escape" && popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  };
  window.addEventListener("keydown", handleKeyDown);

  // Initialize map
  useEffect(() => {
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: getBaseMapStyle(basemap),
      center: [0, 0],
      pitch: 0,
      bearing: 0,
      maxZoom: 16,
      interactive: true,
    });

    mapRef.current = map;
    styleLoadingRef.current = true;

    // Handle style loading
    const handleStyleLoad = () => {
      styleLoadingRef.current = false;

      // Add terrain for MapTiler basemap if 3D is enabled
      if (basemap === "maptiler" && show3D) {
        map.addSource("mapbox-dem", {
          type: "raster-dem",
          url: "https://api.maptiler.com/tiles/terrain-rgb/tiles.json?key=HXgK2fqnmExz8Z5kpMyR",
          tileSize: 512,
          maxzoom: 14,
        });

        map.setTerrain({
          source: "mapbox-dem",
          exaggeration: 1.5,
        });
      }

      if (geojsonData.length > 0) {
        addGeoJSONLayers(map, geojsonData, showLines, visualizationSettings);
      }
    };

    map.on("style.load", handleStyleLoad);

    return () => {
      map.off("style.load", handleStyleLoad);
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [basemap, showLines, visualizationSettings]);

  // click handler for new run creation
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedResortId) return;

    // Create new click handler
    clickHandlerRef.current = (mapClickEvent) => {
      const point = map.project(mapClickEvent.lngLat);
      setNewRunPosition({
        lng: mapClickEvent.lngLat.lng,
        lat: mapClickEvent.lngLat.lat,
        x: point.x,
        y: point.y,
      });
    };

    // Add new click handler
    map.on("click", clickHandlerRef.current);

    // Cleanup
    return () => {
      if (clickHandlerRef.current) {
        map.off("click", clickHandlerRef.current);
      }
    };
  }, [selectedResortId, basemap]);

  // hover and longest run handler
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clean up existing marker if no hover location
    if (!hoverLocation) {
      if (hoverMarkerRef.current) {
        hoverMarkerRef.current.remove();
        hoverMarkerRef.current = null;
      }

      // Hide longest run layer if it exists
      if (map.getLayer("longest-run")) {
        map.setLayoutProperty("longest-run", "visibility", "none");
      }
      return;
    }

    // Handle longest run segments (not a simple marker)
    if (hoverLocation.segments) {
      if (map.getLayer("longest-run")) {
        const segmentIds = hoverLocation.segments.map(
          (segment) => segment.properties.id
        );
        map.setFilter("longest-run", [
          "in",
          ["get", "id"],
          ["literal", segmentIds],
        ]);
        map.setLayoutProperty("longest-run", "visibility", "visible");
      }
      return;
    }

    // Handle simple location marker
    if (hoverLocation.lng && hoverLocation.lat) {
      // Create marker if it doesn't exist
      if (!hoverMarkerRef.current) {
        const el = document.createElement("div");
        createRoot(el).render(<StyledMarker />);
        hoverMarkerRef.current = new maplibregl.Marker({ element: el })
          .setLngLat([hoverLocation.lng, hoverLocation.lat])
          .addTo(map);
      } else {
        // Update existing marker position
        hoverMarkerRef.current.setLngLat([
          hoverLocation.lng,
          hoverLocation.lat,
        ]);
      }
    }
  }, [hoverLocation]);

  // Hide new run name popup when markers are toggled
  useEffect(() => {
    setNewRunPosition(null);
  }, [showMarkers]);

  // Handle GeoJSON data
  useEffect(() => {
    const map = mapRef.current;

    if (!map) return;
    if (geojsonData.length === 0) return;

    // Function to add layers once everything is ready
    const addLayersWhenReady = () => {
      if (styleLoadingRef.current) {
        return;
      }

      // Only update if the source data has changed
      const currentSource = map.getSource("gpx-tracks");
      if (currentSource) {
        try {
          currentSource.setData({
            type: "FeatureCollection",
            features: geojsonData,
          });

          // Update layer visibility
          if (map.getLayer("line-uphill")) {
            map.setLayoutProperty(
              "line-uphill",
              "visibility",
              showLines ? "visible" : "none"
            );
          }
          if (map.getLayer("line-downhill")) {
            map.setLayoutProperty(
              "line-downhill",
              "visibility",
              showLines ? "visible" : "none"
            );
          }
        } catch (error) {
          console.error("Error updating source:", error);
        }
        // Only call fitBounds if selectedResort has just changed
        if (prevSelectedResortId.current !== selectedResortId) {
          fitBounds(map, geojsonData, visualizationSettings);
        }
        prevSelectedResortId.current = selectedResortId;
        return;
      }

      addGeoJSONLayers(map, geojsonData, showLines, visualizationSettings);
    };

    addLayersWhenReady();
  }, [geojsonData, selectedResortId]); // Only data and resort changes

  // Calculate marker screen positions on map move/resize
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !resortRuns.length) {
      setMarkerScreenPositions([]);
      return;
    }
    function updatePositions() {
      setMarkerScreenPositions(
        Object.fromEntries(
          resortRuns.map((run) => {
            const lngLat = run.coordinates;
            const point = map.project(lngLat);
            return [run.id, { id: run.id, x: point.x, y: point.y, run }];
          })
        )
      );
    }
    updatePositions();
    map.on("move", updatePositions);
    map.on("resize", updatePositions);
    return () => {
      map.off("move", updatePositions);
      map.off("resize", updatePositions);
    };
  }, [resortRuns, basemap]);

  // Dismiss selected run popup on map click
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const closePopup = () => {
      setSelectedRunId(null);
    };
    map.on("click", closePopup);
    return () => {
      map.off("click", closePopup);
    };
  }, []);

  // Handle terrain/3D toggle
  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleLoadingRef.current) return;

    // Remove existing terrain if 3D is disabled or basemap doesn't support it
    if (!show3D || basemap !== "maptiler") {
      if (map.getTerrain()) {
        map.setTerrain(null);
      }
      return;
    }

    // Add terrain for MapTiler basemap when 3D is enabled
    if (!map.getSource("mapbox-dem")) {
      map.addSource("mapbox-dem", {
        type: "raster-dem",
        url: "https://api.maptiler.com/tiles/terrain-rgb/tiles.json?key=HXgK2fqnmExz8Z5kpMyR",
        tileSize: 512,
        maxzoom: 14,
      });
    }

    map.setTerrain({
      source: "mapbox-dem",
      exaggeration: 1.5,
    });
  }, [basemap, show3D]);

  return (
    <MapContainer ref={mapContainerRef}>
      {showMarkers && (
        <RunMarkers
          markerScreenPositions={markerScreenPositions}
          setSelectedRunId={setSelectedRunId}
        />
      )}
      {selectedRunId ? (
        <RunPopup
          selectedResortId={selectedResortId}
          setSelectedRunId={setSelectedRunId}
          marker={markerScreenPositions[selectedRunId]}
          setResortRuns={setResortRuns}
        />
      ) : null}
      {newRunPosition && !selectedRunId && showMarkers && (
        <NewRunPopup
          selectedResortId={selectedResortId}
          position={newRunPosition}
          setResortRuns={setResortRuns}
          onClose={() => setNewRunPosition(null)}
        />
      )}
    </MapContainer>
  );
}
